import React from 'react';
import { useTranslation } from 'Shared/Hooks/useTranslation';
import { hot } from 'react-hot-loader/root';
import isPast from 'date-fns/is_past';

import { Container, Row, Col } from 'crehana-ui/GridV2';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import Heart3 from 'crehana-icons/Heart3';
import Heart from 'crehana-icons/Heart';
import Gift2 from 'crehana-icons/Gift2';
import Share from 'crehana-icons/Share';
import ShoppingCartLight from 'crehana-icons/ShoppingCartLight';
import ShoppingCartAdded from 'crehana-icons/ShoppingCartAdded';
import { baseLighter, redMain } from 'crehana-ui/styles/colors';

import useSavedCards from 'Jsx/Shared/Hooks/useSavedCards';
import usePageView from 'Jsx/Analytics/UserActivity/usePageView';
import Loadable from 'Jsx/Shared/Loadable';
import { ProductsContext } from 'Jsx/ContextApis/Products';
import { trackProduct, formatProduct } from 'Jsx/Utils/buyProduct';
import mixpanel from 'Jsx/Utils/mixpanelTracking';
import {
  eventNames,
  eventSources,
  setFbEventToLocalStorage,
} from 'Jsx/Utils/facebookTracking/facebookTracking';
import { MetaTags as MetaTagsType } from 'Jsx/global.d.ts';
import { ObjectQueryParams } from 'Jsx/Utils/parseQueryStrings';
import MetaTags from 'Jsx/Shared/SEO/MetaTags';
import { UserType } from 'Jsx/Types/UserType';
import useIsClient from 'Jsx/Shared/Hooks/useIsClient';
import CoursesInBundle from 'Jsx/Shared/CoursesInBundle';
import matchMediaQuery from 'Jsx/Utils/matchMediaQuery';

import { usePricesContext } from 'Jsx/CourseDetail/Context/Prices';
import useUserCredits from 'Jsx/CourseDetail/hooks/useUserCredits';
import useEnrollmentData from 'Jsx/CourseDetail/hooks/useEnrollmentData';
import useCanShowAccessPreview from 'Jsx/CourseDetail/hooks/useCanShowAccessPreview';
import { trackAnalytics } from 'Jsx/CourseDetail/utils';
import { Course } from '../types';
import locales from '../i18n/localeEs';

import TransactionButtons from './TransactionButtons';
import DialogVideoPlayer from './DialogVideoPlayer';
import MainSection from './MainSection';
import InfoSection from './InfoSection';
import ReviewsSection from './ReviewsSection';
import FeaturedReview from './FeaturedReview';
import CourseCover from './CourseCover';
import MembershipPromo from './MembershipPromo';
import FloatingCard from './FloatingCard/index';
import StickyBottomBar from './StickyBottomBar';
import FreeCourseFAQs from './FreeCourseFAQs';
import Questions from './Questions';
import StickyMainSection from './StickyMainSection';
import Requirements from './Requirements';
import LessonsSection from './LessonsSection';
import Projects from './Projects';
import LegacyUI from './LegacyUI';
import SmallUpselling from '../components/SmallUpselling';
import Trasncript from 'Jsx/CourseDetail/components/Transcript';

const translations = locales.es;

export type AppProps = {
  course: Course;
  user: UserType;
  countryPrefix: string;
  client: any;
  userLanguage: any;
  metaTags: MetaTagsType;
  queryParams: ObjectQueryParams;
  canShowMembershipFeature: boolean;
  isCouponCampaign?: boolean;
  isBundleCampaign?: boolean;
  isLegacyUI?: boolean;
};

const App = ({
  course,
  user,
  metaTags,
  queryParams,
  canShowMembershipFeature,
  isCouponCampaign,
  countryPrefix,
  isBundleCampaign,
  isLegacyUI = true,
}: AppProps) => {
  usePageView({
    type: 'course',
    id: course.originalId,
    pageData: {
      data: {
        category: course.categoryObj?.slug,
        sub_category: course.subcategory?.slug,
      },
      currency_code: course.prices?.currencyCode,
      country_code: countryPrefix,
    },
  });
  const { t } = useTranslation();

  const { prices } = usePricesContext();
  const { creditsAvailable, isFetchingUserCredits } = useUserCredits();
  const [isSticky, setIsSticky] = React.useState<boolean>(false);
  const [isPaymentDialogOpen, setPaymentDialogOpen] = React.useState(false);
  const isCoursePromoActive = !isPast(course.dueDate);
  const {
    canEnroll,
    isEnrollment,
    loading: loadingEnrollmentData,
  } = useEnrollmentData(course);
  const { canShowAccessPreview } = useCanShowAccessPreview(course);
  const { savedCards, loading: isFetchingSavedCards } = useSavedCards({
    isLogged: !!user,
  });
  const product = formatProduct(course, 'course');
  const userHasOrganization = user?.hasOrganization;
  const isBuyable =
    !isEnrollment &&
    !canEnroll &&
    course.courseType !== 'temporal-access' &&
    !userHasOrganization;
  const [featuredReviewId, setFeaturedReviewId] = React.useState(-99999);
  const [shareModal, setShareModal] = React.useState(false);
  const [giftCardModal, setGiftCardModal] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const [trailerOpen, setTrailerOpen] = React.useState(false);
  const {
    wishlistState: { items },
    shoppingCartState: { courseIdsInCart },
    toggleWishlistItem,
    addCourseToCart,
    removeCourseFromCart,
  } = React.useContext(ProductsContext);
  const isClient = useIsClient();
  let matchMedium = false;

  if (isClient) {
    matchMedium = matchMediaQuery('md').matches;
  }
  const canShowPaymentDialog = mounted && !matchMedium && !isCouponCampaign;
  const canRenderCountdown =
    isBuyable &&
    isCoursePromoActive &&
    course.courseType !== 'membership' &&
    course.courseType !== 'temporal-access';
  const canShowOneClickPurchaseButton =
    isBuyable &&
    savedCards.length > 0 &&
    !canShowAccessPreview &&
    creditsAvailable === 0;
  const isAppLoading =
    isFetchingSavedCards || isFetchingUserCredits || loadingEnrollmentData;

  React.useEffect(() => {
    // use effect to control sticky header
    const subHeaderElement = document.querySelector('#sub-header-element');

    const observer = new IntersectionObserver(
      ([e]) => setIsSticky(e.intersectionRatio < 1),
      {
        threshold: 1.0,
        rootMargin: window.innerHeight > 500 ? '10px 0px' : '150px 0px',
      },
    );

    if (subHeaderElement) {
      observer.observe(subHeaderElement);
    }

    setMounted(true);
    trackAnalytics(course);
    mixpanel.track(mixpanel.EVENTS.RENDER_PAGE, {
      page: 'Course Detail',
      courseDetailVersion: 3,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const showCourseTrailer = () => {
    setTrailerOpen(true);
  };

  const hideCourseTrailer = () => {
    setTrailerOpen(false);
  };

  const canRenderVideoPlayer = Boolean(
    course.hideVideoPlayer || (queryParams && queryParams.hideVideoPlayer),
  );

  const toggleAddToWishlist = () => {
    if (user) {
      toggleWishlistItem(course);
    } else {
      window.showLoginModal();
    }
  };

  const showGiftCardDialog = () => {
    setGiftCardModal(true);
  };

  const hideGiftCardDialog = () => {
    setGiftCardModal(false);
  };

  const showShareDialog = () => {
    setShareModal(true);
  };

  const isInShoppingCart = courseIdsInCart.includes(course.originalId);
  const isInWishlist = Boolean(
    items.map(el => el.originalId).includes(course.originalId),
  );
  const toggleShoppingCart = () => {
    if (!isInShoppingCart) {
      addCourseToCart({ ...course, courseId: course.originalId });
      try {
        if (window.gtag) {
          window.gtag('event', 'Add to shopping cart', {
            send_to: 'UA-60829200-1',
            event_category: 'PAYG',
            event_label: 'shopping cart',
          });
        }
      } catch (error) {
        console.log(error);
      }
    } else {
      removeCourseFromCart({ ...course, courseId: course.originalId });
    }
  };

  const WishlistButton = (
    <button
      type="button"
      onClick={toggleAddToWishlist}
      className="mx-16 cursor-pointer flex flex-col items-center"
    >
      <div className="flex items-center" style={{ height: 22 }}>
        {isInWishlist ? (
          <Heart color={redMain} size={18} />
        ) : (
          <Heart3 color={baseLighter} size={16} />
        )}
      </div>
      <p className="mt-4 font-subtitle3 text-base-lighter">
        {isInWishlist
          ? t('REMOVE', {
              defaultValue: translations.REMOVE,
            })
          : t('I_WANT_IT', {
              defaultValue: translations.I_WANT_IT,
            })}
      </p>
    </button>
  );

  const ShareButton =
    course.courseType === 'free' ? (
      <button
        type="button"
        onClick={showShareDialog}
        className="mx-16 flex items-center outline-none"
      >
        <Share color={baseLighter} size={16} className="mr-16" />
        <p className="font-subtitle3 text-base-lighter">
          {t('SHARE_FREE_COURSE', {
            defaultValue: translations.SHARE_FREE_COURSE,
          })}
        </p>
      </button>
    ) : (
      <button
        type="button"
        onClick={showShareDialog}
        className="mx-16 flex flex-col items-center outline-none"
      >
        <div className="flex items-center" style={{ height: 22 }}>
          <Share color={baseLighter} size={16} />
        </div>
        <p className="mt-4 font-subtitle3 text-base-lighter">
          {t('SHARE', {
            defaultValue: translations.SHARE,
          })}
        </p>
      </button>
    );

  const CartButton = (
    <button
      type="button"
      onClick={toggleShoppingCart}
      className="mx-16 cursor-pointer flex flex-col items-center"
    >
      <div style={{ height: 22 }}>
        {isInShoppingCart ? (
          <ShoppingCartAdded color={baseLighter} size={22} />
        ) : (
          <ShoppingCartLight color={baseLighter} size={22} />
        )}
      </div>
      <p className="mt-4 font-subtitle3 text-base-lighter">
        {isInShoppingCart
          ? t('REMOVE', {
              defaultValue: translations.REMOVE,
            })
          : t('ADD', {
              defaultValue: translations.ADD,
            })}
      </p>
    </button>
  );

  const GiftButton = (
    <button
      type="button"
      onClick={showGiftCardDialog}
      className="mx-16 cursor-pointer flex flex-col items-center"
    >
      <div className="flex items-center" style={{ height: 22 }}>
        <Gift2 color={baseLighter} size={16} />
      </div>
      <p className="mt-4 font-subtitle3 text-base-lighter">
        {t('GIFT', {
          defaultValue: translations.GIFT,
        })}
      </p>
    </button>
  );

  /**
   * Show payment dialog for mobile devices
   */
  const showPaymentDialog = () => {
    try {
    //eslint-disable-line
      const fbTrackPayload = {
        content_name: course.title,
        content_ids: course.originalId,
        content_type: 'course',
        value: prices?.price || course.prices.price,
        currency: prices?.currencySymbol || course.prices.currencySymbol,
      };

      setFbEventToLocalStorage({
        eventName: eventNames.GO_TO_CHECKOUT_POPUP,
        sourcePage: eventSources.COURSE_DETAIL,
        fbTrackPayload,
      });

      if (window.gtag) {
        window.gtag('event', 'Comprar ahora', {
          send_to: 'UA-60829200-1',
          event_category: 'PAYG',
          event_label: 'Abre checkout',
          value: course.price,
        });
      }
    } catch (error) {
      console.log(error);
    }
    setPaymentDialogOpen(true);
    trackProduct(product);
    if (window.forceDontLeaveNotificationToHide) {
      window.forceDontLeaveNotificationToHide();
    }
  };

  /**
   * Hide payment dialog for mobile devices
   */
  const hidePaymentDialog = () => {
    //eslint-disable-line
    setPaymentDialogOpen(false);
  };

  const renderTransactionButtons = () => {
    if (isEnrollment) {
      return (
        <PrimaryButton
          href={course.dashboardUrl}
          label={t('GO_TO_COURSE')}
          full
        />
      );
    }

    if (!canEnroll && userHasOrganization) {
      return (
        <div>
          <p className="font-body4">
            Solicita al administrador de tu organización el acceso a este curso.
          </p>
        </div>
      );
    }

    if (canShowOneClickPurchaseButton) {
      return (
        <Loadable
          module={() =>
            import(
              /* webpackChunkName: "one-click-purchase" */ './BuyBtnsOnePurchase'
            )
          }
          course={course}
          userId={user?.originalId}
          customHandleOtherPaymentsMethods={
            canShowPaymentDialog ? showPaymentDialog : undefined
          }
          savedCards={savedCards}
        />
      );
    }

    return (
      <TransactionButtons
        canRenderAddToCartButton={isBuyable}
        renderCourseInfo={
          <InfoSection
            totalLessons={course.totalLessons}
            totalDuration={course.duration}
            totalAttachments={course.totalAttachment}
            iSStickyCard
          />
        }
        canShowAccessPreview={canShowAccessPreview}
        course={course}
        user={user}
        showPaymentDialog={showPaymentDialog}
        canShowPaymentDialog={canShowPaymentDialog}
        isBuyable={isBuyable}
        addToCartTheme="b"
      />
    );
  };

  const BuyButtons = renderTransactionButtons();

  const lastCourseReq = course.requirements && course.requirements.slice(-1);

  return (
    <div>
      <MetaTags {...metaTags} />
      <main className="relative">
        {!canRenderVideoPlayer && (
          <CourseCover
            coverUrl={course.promoImage}
            showCourseTrailer={showCourseTrailer}
          />
        )}

        <StickyMainSection course={course} isSticky={isSticky} />

        <Container
          style={{ zIndex: 20 }}
          className="absolute h-full left-0 right-0 m-auto top-0 pointer-events-none"
        >
          <Row nowrap className="sticky top-0">
            <Col
              xs={12}
              sm={10}
              md={6}
              lg={8}
              offset={{ xs: 0, sm: 1, md: 0, lg: 0 }}
            />
            <Col
              xs={12}
              md={6}
              lg={4}
              offset={{ xs: 0, sm: 0, lg: 1 }}
              className="hidden md:block"
            >
              <FloatingCard
                className="hidden md:block"
                course={course}
                showCourseTrailer={showCourseTrailer}
                isBuyable={isBuyable}
                CartButton={CartButton}
                GiftButton={GiftButton}
                WishlistButton={WishlistButton}
                canShowMembershipFeature={canShowMembershipFeature && isBuyable}
                renderTransactionButtons={renderTransactionButtons}
                isAppLoading={isAppLoading}
                canRenderCountdown={canRenderCountdown}
                ShareButton={ShareButton}
                userHasOrganization={userHasOrganization}
                canShowAddToPackUI={isBundleCampaign}
                canEnroll={canEnroll}
                isSticky={isSticky}
              />
            </Col>
          </Row>
        </Container>
        <MainSection
          course={course}
          isInWishlist={Boolean(
            items.map(el => el.originalId).includes(course.originalId),
          )}
          toggleAddToWishlist={toggleAddToWishlist}
          showShareDialog={showShareDialog}
          showGiftCardDialog={showGiftCardDialog}
          canRenderCountdown={canRenderCountdown}
          BuyButtons={BuyButtons}
          isBuyable={isBuyable}
          CartButton={CartButton}
          GiftButton={GiftButton}
          WishlistButton={WishlistButton}
          canShowMembershipFeature={canShowMembershipFeature && isBuyable}
          isAppLoading={isAppLoading}
          canShowAddToPackUI={isBundleCampaign}
        />
        <div id="sub-header-element" />
        <Container>
          <Row nowrap>
            <Col
              xs={12}
              sm={10}
              md={6}
              lg={8}
              offset={{ xs: 0, sm: 1, md: 0, lg: 0 }}
            >
              {isLegacyUI ? (
                <LegacyUI course={course} />
              ) : (
                <React.Fragment>
                  {course.requirements &&
                    course.requirements
                      .sort((a, b) => a.position - b.position)
                      .slice(0, -1)
                      .map((requirement, i) => (
                        <Requirements
                          key={i.toString()}
                          {...requirement}
                          isFirstElement={i === 0}
                        />
                      ))}
                  <InfoSection
                    totalLessons={course.totalLessons}
                    totalDuration={course.duration}
                    totalAttachments={course.totalAttachment}
                    className="border-b py-32 border-solid border-gray-light"
                  />
                  {course.requirements && (
                    <Requirements {...lastCourseReq[0]} />
                  )}
                </React.Fragment>
              )}
              <Projects courseId={course.originalId} />
              <LessonsSection
                course={course}
                user={user}
                BuyButtons={BuyButtons}
                mounted={mounted}
              />
              <FeaturedReview
                courseId={course.originalId}
                setFeaturedReviewId={setFeaturedReviewId}
              />
              {!isEnrollment &&
                !isBundleCampaign &&
                !user?.isPremiumMember &&
                course.courseType === 'paid' &&
                user?.segmentation !== 'PAYG' &&
                !user?.organization && (
                  <SmallUpselling
                    countryPrefix={countryPrefix}
                    courseId={course.originalId}
                    parentComponent="Main app"
                  />
                )}
              {!user?.isPremiumMember &&
                user?.segmentation === 'PAYG' &&
                !user?.organization && <MembershipPromo />}

              {course.courseType === 'free' ? <FreeCourseFAQs /> : null}
              <ReviewsSection
                courseId={course.originalId}
                averageScore={course.score}
                featuredReviewId={featuredReviewId}
              />
              <Questions countryPrefix={countryPrefix} />
              {course.transcript && (
                <Trasncript textTranscript={course.transcript} />
              )}
            </Col>
            <Col
              xs={12}
              md={6}
              lg={4}
              offset={{ xs: 0, sm: 0, lg: 1 }}
              className="relative hidden md:block"
            />
          </Row>
        </Container>
      </main>
      {!user?.hasOrganization && !user?.isPremiumMember ? (
        <StickyBottomBar
          courseDueDate={course.dueDate}
          className="lg:hidden"
          loadingCanEnroll={isAppLoading}
          showPaymentDialog={showPaymentDialog}
          BuyButtons={BuyButtons}
          isFreeCourse={course.courseType === 'free'}
          canEnroll={canEnroll}
        />
      ) : null}
      {canShowPaymentDialog && isPaymentDialogOpen && (
        <Loadable
          module={() =>
            import(
              /* webpackChunkName: "course-detail-checkout-dialog"  */ 'Jsx/Shared/CheckoutDialog'
            )
          }
          paymentDialogVisibility={isPaymentDialogOpen}
          hidePaymentDialog={hidePaymentDialog}
          paymentSource={eventSources.COURSE_DETAIL_POP_UP_VIEW}
          itemData={{
            itemType: 'course',
            itemId: course.originalId,
          }}
        />
      )}
      <Loadable
        module={() =>
          import(
            /* webpackChunkName: "course-detail-share-modal" */ './ShareModal'
          )
        }
        promoImage={course.promoImage}
        title={course.title}
        professor={course.professor}
        open={shareModal}
        onDismiss={() => setShareModal(false)}
      />
      <Loadable
        module={() =>
          import(
            /* webpackChunkName: "course-detail-gift-card-modal" */ 'Jsx/Shared/GiftCardModal'
          )
        }
        open={giftCardModal}
        onRequestClose={hideGiftCardDialog}
        giftcardData={{
          itemId: course.originalId,
          itemType: 'course',
          title: course.title,
        }}
        user={user}
      />
      <DialogVideoPlayer
        user={user}
        open={trailerOpen}
        hideCourseTrailer={hideCourseTrailer}
        course={course}
      />
      {isBundleCampaign && <CoursesInBundle dockToBottom />}
    </div>
  );
};

export default hot(App);
